import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSession } from '../../lib/session.js'
import { useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'
import { useDismiss } from './useDismiss.js'

export type MessageTemplate = RouterOutputs['messageTemplates']['list'][number]

/**
 * The saved messages for this channel. Fetched when the menu opens, not with
 * the window. Picking one over words already written asks first: replace
 * them, or put the template in at the cursor.
 */
export function TemplatesMenu({
  channel,
  hasText,
  onPick,
}: {
  channel: 'EMAIL' | 'SMS'
  hasText: boolean
  onPick: (template: MessageTemplate, mode: 'replace' | 'insert') => void
}) {
  const trpc = useTRPC()
  const { can } = useSession()
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<MessageTemplate | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const close = () => {
    setOpen(false)
    setPending(null)
  }
  useDismiss(wrap, open, close)

  const list = useQuery({ ...trpc.messageTemplates.list.queryOptions({ channel }), enabled: open, staleTime: 60_000 })

  const pick = (template: MessageTemplate) => {
    if (!hasText) {
      onPick(template, 'replace')
      close()
      return
    }
    setPending(template)
  }

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        aria-expanded={open}
        className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
        onClick={() => (open ? close() : setOpen(true))}
      >
        Templates
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Templates"
          className="absolute bottom-full left-0 z-30 mb-2 w-80 overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-ink-200"
        >
          {pending ? (
            <div className="flex flex-col gap-3 p-3.5">
              <p className="text-[13px] text-ink-700">
                There is already a message. What should <span className="font-semibold">{pending.name}</span> do?
              </p>
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  className="rounded-lg bg-brand-800 px-3 py-2 text-[13px] font-medium text-white hover:bg-brand-700"
                  onClick={() => {
                    onPick(pending, 'replace')
                    close()
                  }}
                >
                  Replace the message
                </button>
                <button
                  type="button"
                  className="rounded-lg px-3 py-2 text-[13px] font-medium text-ink-800 ring-1 ring-ink-300 ring-inset hover:bg-ink-50"
                  onClick={() => {
                    onPick(pending, 'insert')
                    close()
                  }}
                >
                  Insert at the cursor
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-[12px] text-ink-500 hover:text-ink-900"
                  onClick={() => setPending(null)}
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <>
              <ul className="max-h-72 overflow-y-auto p-1">
                {list.isLoading ? (
                  <li className="flex items-center gap-2 px-2.5 py-2 text-[13px] text-ink-500">
                    <Spinner className="size-4" /> Loading…
                  </li>
                ) : list.isError ? (
                  <li className="px-2.5 py-2 text-[13px] text-red-700">The templates did not load.</li>
                ) : (list.data ?? []).length === 0 ? (
                  <li className="px-2.5 py-2 text-[13px] text-ink-500">No {channel === 'SMS' ? 'text' : 'email'} templates yet.</li>
                ) : (
                  list.data!.map((template) => (
                    <li key={template.id}>
                      <button
                        type="button"
                        className="flex w-full flex-col gap-0.5 rounded-md px-2.5 py-2 text-left hover:bg-ink-50"
                        onClick={() => pick(template)}
                      >
                        <span className="text-[13px] font-semibold text-ink-900">{template.name}</span>
                        <span className="line-clamp-2 text-[12px] text-ink-500">
                          {template.subject ? `${template.subject} — ` : ''}
                          {template.bodyText}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
              {can('MESSAGE_TEMPLATE_MANAGE') ? (
                <div className="border-t border-ink-100 p-1">
                  <Link
                    to="/settings?tab=templates"
                    className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] font-medium text-ink-700 hover:bg-ink-50"
                    onClick={close}
                  >
                    <Icon name="note" className="size-4 text-ink-400" />
                    Edit templates
                  </Link>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}
